/**
 * AlarmCreate.tsx — standalone alarm creation page.
 * Watchpoint select → catalog fetch → condition builder → severity/failures/cooldown
 * → channel multiselect → submit.
 *
 * Context variant: when watchpointId prop is passed (from WatchpointAlarmsSection),
 * watchpoint select is hidden and POST goes to /watchpoints/:id/alarms.
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  CardDescription,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Switch,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useWatchpoints,
  useAlarmFieldCatalogByType,
  useCreateAlarm,
  useCreateWatchpointAlarm,
  useChannels,
  usePlaybooks,
  type WatchpointType,
  type AlarmSeverity,
} from '@/api/healthcheck';
import { hcExtractError } from '@/api/healthcheck';
import { ConditionBuilder } from './ConditionBuilder';
import { ChannelMultiselect } from './ChannelMultiselect';
import { AlarmSummary } from './AlarmSummary';
import { serializeConditions, validateRows, recoerceRows } from './condition-serializer';
import type { ConditionRow } from './condition-serializer';

const SEVERITIES: AlarmSeverity[] = ['info', 'warning', 'critical'];

interface Props {
  /** Pre-filled watchpoint (from detail context — hides the watchpoint selector). */
  watchpointId?: string;
  watchpointType?: WatchpointType;
  /** Seed condition rows (e.g. "Apply as threshold" from the baseline panel). */
  initialRows?: ConditionRow[];
  onSuccess?: () => void;
}

export function AlarmCreate({ watchpointId: prefilledId, watchpointType: prefilledType, initialRows, onSuccess }: Props) {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const embedded = !!prefilledId;

  // ---- Form state --------------------------------------------------------
  const [selectedWpId, setSelectedWpId] = useState(prefilledId ?? '');
  const [selectedWpType, setSelectedWpType] = useState<WatchpointType | undefined>(prefilledType);
  const [name, setName] = useState('');
  const [severity, setSeverity] = useState<AlarmSeverity>('warning');
  const [consecutiveFailures, setConsecutiveFailures] = useState(3);
  const [cooldownMinutes, setCooldownMinutes] = useState(5);
  const [notifyOnRecovery, setNotifyOnRecovery] = useState(true);
  const [rows, setRows] = useState<ConditionRow[]>([]);
  const [mode, setMode] = useState<'and' | 'or'>('and');
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [playbookId, setPlaybookId] = useState<string>('');
  const [rawFallback, setRawFallback] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ---- Data fetching -----------------------------------------------------
  const { data: wpData } = useWatchpoints(projectId, {});
  const watchpoints = wpData?.items ?? [];

  const { data: catalog, isLoading: catalogLoading } = useAlarmFieldCatalogByType(selectedWpType);
  const { data: channels = [] } = useChannels();
  const { data: playbooks = [] } = usePlaybooks(projectId);

  // ---- Mutations ---------------------------------------------------------
  const createAlarm = useCreateAlarm({
    onSuccess: () => {
      if (onSuccess) { onSuccess(); return; }
      navigate(`${base}/alarms`);
    },
    onError: (e) => setSubmitError(hcExtractError(e, 'Create failed')),
  });

  const createWpAlarm = useCreateWatchpointAlarm(prefilledId ?? '', {
    onSuccess: () => { if (onSuccess) onSuccess(); },
    onError: (e) => setSubmitError(hcExtractError(e, 'Create failed')),
  });

  // ---- Handlers ----------------------------------------------------------
  const handleWpChange = (wpId: string) => {
    setSelectedWpId(wpId);
    const wp = watchpoints.find((w) => w.id === wpId);
    setSelectedWpType(wp?.type);
    setRows([]);
    setMode('and');
    setRawFallback(null);
  };

  // M1: Seed one empty row when catalog first loads (useEffect, not render body).
  useEffect(() => {
    if (!catalog) return;
    if (rows.length > 0 || rawFallback !== null) return;
    // "Apply as threshold" seeds a fixed-threshold row from the baseline panel.
    if (initialRows && initialRows.length) { setRows(initialRows); return; }
    const first = catalog.condition_fields[0];
    if (first) setRows([{ field: first.name, operator: first.operators[0] ?? '', value: '' }]);
    // Only run when catalog identity changes (new watchpoint type selected)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog]);

  const validate = (): boolean => {
    setShowErrors(true);
    if (!name.trim()) return false;
    if (!selectedWpId) return false;
    // M2: catalog must be loaded before submit — eval_level determines wire format
    if (selectedWpType && !catalog) return false;
    if (rawFallback !== null) return rawFallback.trim().length > 0;
    const errors = validateRows(rows);
    if (errors.length > 0) return false;
    if (consecutiveFailures < 1) return false;
    if (cooldownMinutes < 0) return false;
    return true;
  };

  const handleSubmit = () => {
    setSubmitError(null);
    if (!validate()) return;

    let conditions: unknown;
    if (rawFallback !== null) {
      try { conditions = JSON.parse(rawFallback); }
      catch { setSubmitError('Invalid JSON in conditions.'); return; }
    } else {
      // H1: re-coerce all row values by field.kind before serialising.
      // Guarantees number→JS number, bool→boolean regardless of how rows were loaded.
      const fields = catalog?.condition_fields ?? [];
      const coercedRows = recoerceRows(rows, fields);
      const errors = validateRows(coercedRows);
      if (errors.length > 0) {
        setSubmitError(errors.map((e) => e.message).join(' '));
        return;
      }
      // catalog is guaranteed non-null here (validate() would have returned false)
      conditions = serializeConditions(coercedRows, catalog!.eval_level, mode);
    }

    const payload = {
      name: name.trim(),
      severity,
      conditions,
      consecutive_failures: consecutiveFailures,
      cooldown_minutes: cooldownMinutes,
      notify_on_recovery: notifyOnRecovery,
      channel_ids: channelIds,
      playbook_id: playbookId || null,
    };

    if (embedded && prefilledId) {
      createWpAlarm.mutate(payload);
    } else {
      createAlarm.mutate({ ...payload, watchpoint_id: selectedWpId });
    }
  };

  // M2: also block submit while catalog is resolving (wrong eval_level = wrong wire format)
  const pending = createAlarm.isPending || createWpAlarm.isPending;
  const submitBlocked = pending || (!!selectedWpType && catalogLoading);
  const selectedChannelNames = channelIds
    .map((id) => channels.find((c) => c.id === id)?.name ?? '')
    .filter(Boolean);

  return (
    <Container size="xl">
      {!embedded && (
        <PageHeader
          icon={<Bell size={20} />}
          breadcrumbs={
            <Breadcrumbs>
              <BreadcrumbItem asChild><Link to={`${base}/alarms`}>Alarms</Link></BreadcrumbItem>
              <BreadcrumbItem current>New alarm</BreadcrumbItem>
            </Breadcrumbs>
          }
          title="New alarm"
          description="Create an alert rule that fires when monitor conditions are met."
        />
      )}

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 720 }}>
        {/* Name + watchpoint */}
        <Card flat>
          <CardHeader>
            <CardTitle>Rule details</CardTitle>
            <CardDescription>Name this alert rule and choose which monitor it watches.</CardDescription>
          </CardHeader>
          <CardBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <Field
                label="Name"
                required
                error={showErrors && !name.trim() ? 'Name is required.' : undefined}
              >
                {(p) => (
                  <Input
                    {...p}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="High error rate"
                    maxLength={255}
                    style={{ maxWidth: 420 }}
                    invalid={showErrors && !name.trim()}
                  />
                )}
              </Field>

              {!embedded && (
                <Field
                  label="Monitor"
                  required
                  error={showErrors && !selectedWpId ? 'Choose a monitor.' : undefined}
                >
                  {() => (
                    <Select value={selectedWpId} onValueChange={handleWpChange}>
                      <SelectTrigger style={{ maxWidth: 420 }} invalid={showErrors && !selectedWpId}>
                        <SelectValue placeholder="Select a monitor…" />
                      </SelectTrigger>
                      <SelectContent>
                        {watchpoints.map((wp) => (
                          <SelectItem key={wp.id} value={wp.id}>
                            {wp.name}{' '}
                            <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)' }}>
                              ({wp.type})
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Condition builder — shown once a watchpoint type is known */}
        {selectedWpType && (
          <Card flat>
            <CardHeader>
              <CardTitle>Conditions</CardTitle>
              <CardDescription>Alert fires when these conditions are met by the monitor's data.</CardDescription>
            </CardHeader>
            <CardBody>
              {catalogLoading ? (
                <Skeleton style={{ height: 80 }} />
              ) : catalog ? (
                <ConditionBuilder
                  catalog={catalog}
                  rows={rows}
                  mode={mode}
                  onRowsChange={setRows}
                  onModeChange={setMode}
                  showErrors={showErrors}
                  rawFallback={rawFallback ?? undefined}
                  onRawFallbackChange={setRawFallback}
                />
              ) : (
                <p style={{ margin: 0, color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)' }}>
                  Could not load field catalog for this monitor type.
                </p>
              )}
            </CardBody>
          </Card>
        )}

        {/* Severity + thresholds */}
        <Card flat>
          <CardHeader>
            <CardTitle>Alert behaviour</CardTitle>
            <CardDescription>Severity level and failure/cooldown thresholds.</CardDescription>
          </CardHeader>
          <CardBody>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--vn-space-4)', maxWidth: 540 }}>
              <Field label="Severity">
                {() => (
                  <Select value={severity} onValueChange={(v) => setSeverity(v as AlarmSeverity)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SEVERITIES.map((s) => (
                        <SelectItem key={s} value={s} style={{ textTransform: 'capitalize' }}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field
                label="Consecutive failures"
                description="Alert after N failures in a row."
                error={showErrors && consecutiveFailures < 1 ? 'Min 1.' : undefined}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={1}
                    value={String(consecutiveFailures)}
                    onChange={(e) => setConsecutiveFailures(Math.max(1, Number(e.target.value) || 1))}
                    invalid={showErrors && consecutiveFailures < 1}
                  />
                )}
              </Field>

              <Field
                label="Cooldown (minutes)"
                description="Wait between repeated alerts."
                error={showErrors && cooldownMinutes < 0 ? 'Min 0.' : undefined}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={0}
                    value={String(cooldownMinutes)}
                    onChange={(e) => setCooldownMinutes(Math.max(0, Number(e.target.value) || 0))}
                    invalid={showErrors && cooldownMinutes < 0}
                  />
                )}
              </Field>
            </div>

            {/* Recovery notification toggle */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--vn-space-3)',
                marginTop: 'var(--vn-space-4)',
                cursor: 'pointer',
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
                  Send &ldquo;Recovered&rdquo; notification
                </div>
                <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                  Notify channels when this alarm clears. Off = only alert when it fires.
                </div>
              </div>
              <Switch checked={notifyOnRecovery} onCheckedChange={setNotifyOnRecovery} />
            </label>
          </CardBody>
        </Card>

        {/* Channels */}
        <Card flat>
          <CardHeader>
            <CardTitle>Notification channels</CardTitle>
            <CardDescription>Choose where to send alerts when this rule fires.</CardDescription>
          </CardHeader>
          <CardBody>
            <ChannelMultiselect value={channelIds} onChange={setChannelIds} />
          </CardBody>
        </Card>

        {/* Playbook — attach an auto-remediation playbook */}
        <Card flat>
          <CardHeader>
            <CardTitle>Auto-remediation (optional)</CardTitle>
            <CardDescription>
              Run a playbook automatically when this alarm fires. Disarmed playbooks won't run until armed.
            </CardDescription>
          </CardHeader>
          <CardBody>
            <Field
              label="Playbook"
              description={
                playbooks.length === 0
                  ? 'No playbooks yet — create one from the Playbooks page.'
                  : 'Runs automatically when this alarm fires.'
              }
            >
              {() => (
                <Select
                  value={playbookId || '__none__'}
                  onValueChange={(v) => setPlaybookId(v === '__none__' ? '' : v)}
                  disabled={playbooks.length === 0}
                >
                  <SelectTrigger style={{ maxWidth: 420 }}>
                    <SelectValue placeholder="— None —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {playbooks.map((pb) => (
                      <SelectItem key={pb.id} value={pb.id}>
                        {pb.name}
                        {pb.is_active ? ' (Armed)' : ' (Disarmed)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          </CardBody>
        </Card>

        {/* Live summary */}
        {rows.length > 0 && (
          <AlarmSummary
            rows={rows}
            mode={mode}
            severity={severity}
            consecutiveFailures={consecutiveFailures}
            channelNames={selectedChannelNames}
          />
        )}

        {submitError && (
          <Alert tone="danger" title={submitError} />
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
          <Button
            variant="ghost"
            onClick={() => (embedded ? onSuccess?.() : navigate(`${base}/alarms`))}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitBlocked}>
            {pending ? 'Creating…' : catalogLoading ? 'Loading…' : 'Create alarm'}
          </Button>
        </div>
      </div>
    </Container>
  );
}

